import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Download, Loader2 } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const res = await api.get(`/orders/by-session/${sessionId}`);
          if (res.data.paid) {
            setOrder(res.data);
            setState("done");
            return;
          }
        }
      } catch (e) {
        // keep polling
      }
      attempts += 1;
      if (attempts < 15) timer = setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page">
      <Nav />
      <section className="mx-auto flex min-h-screen max-w-3xl flex-col items-center justify-center px-6 py-40 text-center">
        {state === "checking" && (
          <div data-testid="payment-checking" className="flex flex-col items-center gap-6">
            <Loader2 className="animate-spin text-mist" size={36} />
            <p className="font-display text-3xl text-ivory">Sto confermando il pagamento…</p>
          </div>
        )}
        {state === "done" && order && (
          <div data-testid="payment-confirmed" className="flex flex-col items-center gap-8">
            <CheckCircle2 size={48} className="text-sage" />
            <h1 className="font-display text-4xl text-ivory md:text-5xl">Grazie per il tuo acquisto.</h1>
            <p className="max-w-md text-sm leading-relaxed text-mute md:text-base">
              {order.type === "guide"
                ? `Il pagamento di "${order.product_name}" è confermato. Ti ho inviato anche una email con il link per scaricare il PDF.`
                : `Il pagamento di "${order.product_name}" è confermato. Primo passo: compila il questionario di valutazione qui sotto. Ti ho inviato anche una email con tutti i prossimi passi.`}
            </p>
            {order.download_url && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}${order.download_url}`} data-testid="download-guide-button" className="btn-primary">
                Scarica il PDF <Download size={14} />
              </a>
            )}
            {order.type === "coaching" && (
              <Link to={`/onboarding?session_id=${sessionId}`} data-testid="onboarding-cta" className="btn-primary">
                Compila il questionario di valutazione
              </Link>
            )}
            <Link to="/" data-testid="back-home-link" className="label-caps text-mute transition-colors duration-300 hover:text-ivory">
              Torna alla home
            </Link>
          </div>
        )}
        {(state === "error" || state === "timeout") && (
          <div data-testid="payment-pending" className="flex flex-col items-center gap-8">
            <h1 className="font-display text-4xl text-ivory">Verifica in corso</h1>
            <p className="max-w-md text-sm leading-relaxed text-mute">
              Non sono riuscita a confermare subito il pagamento. Se hai completato l'acquisto, riceverai
              una email di conferma a breve. Per assistenza scrivimi dalla pagina contatti.
            </p>
            <Link to="/contatti" className="btn-outline">Contattami</Link>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
